import { CreditCard, Lock, ShieldOff, type LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { BillingItem } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { RequiredEntitlement } from "@/stores/engine";
import { useRecoveryAction } from "./use-recovery-action";

export type RestrictedReason = "auth" | "billing" | "permission";

export interface RestrictedStateProps {
  reason: RestrictedReason;
  title?: string;
  description?: string;
  /** `reason === "billing"` only — which add-on this action needs, driving
   * both the Checkout item(s) and the default copy. Omit to request either
   * add-on (both items). */
  entitlement?: RequiredEntitlement;
  className?: string;
}

const ICONS: Record<RestrictedReason, LucideIcon> = {
  auth: Lock,
  billing: CreditCard,
  permission: ShieldOff,
};

const ITEMS_FOR: Record<RequiredEntitlement, BillingItem[]> = {
  storage_unlimited: ["storage"],
  models_premium: ["models"],
};

function defaultCopy(reason: RestrictedReason, entitlement?: RequiredEntitlement) {
  if (reason === "auth") {
    return {
      title: "Sign in to continue",
      description: "This needs an account so your work stays saved and synced.",
    };
  }
  if (reason === "permission") {
    return { title: "Restricted", description: "You don't have access to this." };
  }
  const title =
    entitlement === "storage_unlimited"
      ? "Unlimited storage required"
      : entitlement === "models_premium"
        ? "Frontier models required"
        : "Upgrade to unlock this";
  return { title, description: "This is part of a paid add-on to your plan." };
}

/** "You can't do this yet, but here's how" — a whole feature or action gated
 * behind sign-in, a paid add-on, or a permission the user doesn't have. Unlike
 * {@link ErrorState}, nothing went wrong here; the CTA is the point. */
export function RestrictedState({
  reason,
  title,
  description,
  entitlement,
  className,
}: RestrictedStateProps) {
  const { openBilling, signIn, contactSupport, billingEnabled } = useRecoveryAction();
  const Icon = ICONS[reason];
  const copy = defaultCopy(reason, entitlement);
  const billingUnavailable = reason === "billing" && billingEnabled === false;

  let primary: { label: string; onClick: () => void } | null = null;
  if (reason === "auth") {
    primary = { label: "Sign in", onClick: signIn };
  } else if (reason === "permission") {
    primary = { label: "Contact support", onClick: contactSupport };
  } else if (!billingUnavailable) {
    primary = {
      label: "View plans",
      onClick: () => openBilling(entitlement ? ITEMS_FOR[entitlement] : undefined),
    };
  }

  return (
    <div className={cn("flex flex-col items-center gap-2.5 p-6 text-center", className)}>
      <div className="grid h-10 w-10 place-items-center rounded-full bg-muted text-primary">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <p className="font-display text-[15px] text-foreground">{title ?? copy.title}</p>
      <p className="max-w-sm text-[13px] leading-relaxed text-muted-foreground">
        {billingUnavailable
          ? "This deployment doesn't offer self-serve upgrades — reach out if you need this."
          : (description ?? copy.description)}
      </p>
      <div className="mt-1 flex items-center gap-2">
        {primary && (
          <Button type="button" variant="default" size="sm" onClick={primary.onClick}>
            {primary.label}
          </Button>
        )}
        {billingUnavailable && (
          <Button type="button" variant="outline" size="sm" onClick={contactSupport}>
            Contact support
          </Button>
        )}
      </div>
    </div>
  );
}
